import { useState } from "react";
import { cn } from "@/lib/utils";
import { ImageOff } from "lucide-react";

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackClassName?: string;
}

/**
 * SafeImage component with automatic error handling.
 * Shows a fallback UI when the image fails to load.
 */
export function SafeImage({
  src,
  alt,
  className,
  fallbackClassName,
  ...props
}: SafeImageProps) {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted",
          fallbackClassName || className
        )}
      >
        <div className="flex flex-col items-center gap-1 text-muted-foreground/50">
          <ImageOff className="h-6 w-6" />
          <span className="text-xs">Unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      // Sensible defaults for content images (feed, photo strips, admin
      // tables): lazy-load and decode off the main thread. Both come before
      // {...props} so any caller can override (e.g. loading="eager" for an
      // above-the-fold hero).
      loading="lazy"
      decoding="async"
      onError={() => setHasError(true)}
      {...props}
    />
  );
}
